import { ulid } from 'ulid';
import { dispatch } from '../events/dispatcher.js';
import { db } from '../db/db.js';
import type { SubscriptionFrequency } from '../events/types.js';
import type { Subscription } from '../db/schema.js';

const DAY_MS = 86_400_000;

/** v0.4 D：把 frequency 轉成天數，custom 走 customDays。 */
export function frequencyDays(frequency: SubscriptionFrequency, customDays?: number): number {
  switch (frequency) {
    case 'weekly':
      return 7;
    case 'biweekly':
      return 14;
    case 'monthly':
      // 簡化：30 天；視覺上「下次到貨日」會被使用者調整，這只是 default cadence
      return 30;
    case 'custom':
      if (!customDays || customDays <= 0) {
        throw new Error('frequencyDays: custom frequency requires positive customDays');
      }
      return customDays;
  }
}

export interface CreateSubscriptionInput {
  knowledgeId: string;
  defaultName: string;
  frequency: SubscriptionFrequency;
  customDays?: number;
  /** 下次到貨日；省略則用 now + frequencyDays */
  nextDelivery?: Date;
  source: string;
  defaultQuantity: number;
  defaultUnit: string;
  /** 提早幾天進入「該補單了」suggestions；預設 3 */
  leadDays?: number;
}

export async function createSubscription(input: CreateSubscriptionInput): Promise<string> {
  const trimmedName = input.defaultName.trim();
  if (!trimmedName) throw new Error('createSubscription: defaultName required');
  if (input.defaultQuantity <= 0) {
    throw new Error('createSubscription: defaultQuantity must be > 0');
  }
  if (input.frequency === 'custom' && (!input.customDays || input.customDays <= 0)) {
    throw new Error('createSubscription: customDays required for custom frequency');
  }
  const id = ulid();
  const now = Date.now();
  const cadence = frequencyDays(input.frequency, input.customDays);
  const nextDelivery = input.nextDelivery?.getTime() ?? now + cadence * DAY_MS;
  await dispatch('subscription_created', {
    subscriptionId: id,
    knowledgeId: input.knowledgeId,
    defaultName: trimmedName,
    frequency: input.frequency,
    ...(input.customDays !== undefined ? { customDays: input.customDays } : {}),
    nextDelivery,
    source: input.source,
    defaultQuantity: input.defaultQuantity,
    defaultUnit: input.defaultUnit,
    leadDays: input.leadDays ?? 3,
    createdAt: now,
  });
  return id;
}

/**
 * 推進訂閱下次到貨日。
 * @param triggeredBy 'received' = 收貨後自動；'manual' = 使用者點 Settings 中「下一期」
 * @param baseDate 推進的起算日；預設用 sub.nextDelivery（不是 now，避免使用者手動補晚一天就累積延遲）
 */
export async function advanceSubscription(
  subscriptionId: string,
  triggeredBy: 'received' | 'manual' = 'manual',
  baseDate?: Date,
): Promise<void> {
  const sub = await db.subscriptions.get(subscriptionId);
  if (!sub) throw new Error(`advanceSubscription: subscription ${subscriptionId} not found`);
  if (!sub.active) return; // deactivated 不推進；caller 應視情況忽略
  const cadence = frequencyDays(sub.frequency, sub.customDays);
  const baseMs = baseDate?.getTime() ?? sub.nextDelivery;
  const newNextDelivery = baseMs + cadence * DAY_MS;
  await dispatch('subscription_advanced', {
    subscriptionId,
    newNextDelivery,
    triggeredBy,
    advancedAt: Date.now(),
  });
}

export async function deactivateSubscription(subscriptionId: string): Promise<void> {
  const sub = await db.subscriptions.get(subscriptionId);
  if (!sub) throw new Error(`deactivateSubscription: subscription ${subscriptionId} not found`);
  if (!sub.active) return; // idempotent
  await dispatch('subscription_deactivated', {
    subscriptionId,
    deactivatedAt: Date.now(),
  });
}

/**
 * 找「該補單了」的訂閱：active 且 (nextDelivery - leadDays) ≤ now。
 *
 * 排除：已有對應 active 的 ShoppingItem（subscriptionId match 且 status ∈ {planned, ordered, in_transit}）
 * — 避免重複建議同一輪訂閱。
 */
export async function dueSubscriptionsForSuggestion(
  now: number = Date.now(),
): Promise<Subscription[]> {
  const allSubs = await db.subscriptions.toArray();
  if (allSubs.length === 0) return [];
  const allShop = await db.shoppingItems.toArray();
  const activeShopBySub = new Map<string, true>();
  for (const row of allShop) {
    if (!row.subscriptionId) continue;
    if (row.status === 'planned' || row.status === 'ordered' || row.status === 'in_transit') {
      activeShopBySub.set(row.subscriptionId, true);
    }
  }
  const due: Subscription[] = [];
  for (const sub of allSubs) {
    if (!sub.active) continue;
    if (activeShopBySub.has(sub.id)) continue; // 已在採買流程中
    const dueAt = sub.nextDelivery - sub.leadDays * DAY_MS;
    if (dueAt <= now) due.push(sub);
  }
  // 按 nextDelivery 升冪（最近到的在前）
  due.sort((a, b) => a.nextDelivery - b.nextDelivery);
  return due;
}

export interface UpdateSubscriptionInput extends CreateSubscriptionInput {
  subscriptionId: string;
}

export async function updateSubscription(input: UpdateSubscriptionInput): Promise<void> {
  const sub = await db.subscriptions.get(input.subscriptionId);
  if (!sub) throw new Error(`updateSubscription: subscription ${input.subscriptionId} not found`);
  if (!sub.active) throw new Error('cannot update deactivated subscription');

  const trimmedName = input.defaultName.trim();
  if (!trimmedName) throw new Error('updateSubscription: defaultName required');
  if (input.defaultQuantity <= 0) {
    throw new Error('updateSubscription: defaultQuantity must be > 0');
  }
  if (input.frequency === 'custom' && (!input.customDays || input.customDays <= 0)) {
    throw new Error('updateSubscription: customDays required for custom frequency');
  }

  const now = Date.now();
  const cadence = frequencyDays(input.frequency, input.customDays);
  const nextDelivery = input.nextDelivery?.getTime() ?? sub.nextDelivery;

  await dispatch('subscription_updated', {
    subscriptionId: input.subscriptionId,
    knowledgeId: input.knowledgeId,
    defaultName: trimmedName,
    frequency: input.frequency,
    ...(input.customDays !== undefined ? { customDays: input.customDays } : {}),
    nextDelivery,
    source: input.source,
    defaultQuantity: input.defaultQuantity,
    defaultUnit: input.defaultUnit,
    leadDays: input.leadDays ?? 3,
    updatedAt: now,
  });
}
